import { useRef, useState, useMemo } from 'react';
import { Sparkles, Link2, GitMerge, User, Box, Tag, ChevronDown, Send, Lock, MessageSquare, Gauge, Clock, Eye, Paperclip, Download, X, FileText, Zap, Shield, ExternalLink, Bold, Italic, Underline, List, Smile, Mail, Image as ImageIcon, Code, Save, Globe } from 'lucide-react';
import { type Ticket, heatOf, RESOLUTION_CODES } from '@/lib/mock';
import { useMacros, applyVars } from '@/lib/macros';
import { MacroManager } from './MacroManager';
import { thermal, thermalColor } from '@/lib/temp';
import { TimelineSpine } from './TimelineSpine';
import type { CustomField } from '@/lib/customFields';
import { cn } from '@/lib/utils';
import { useAuth } from '@/lib/auth';
import { useUsers } from '@/lib/useUsers';
import { useSettings } from '@/lib/useSettings';
import { useRole } from '@/lib/role';

const STATES = ['Open', 'Assigned', 'In Progress', 'Pending', 'On Hold', 'Resolved', 'Closed'];

export type TicketActions = {
  onStatus: (state: string) => void;
  onAssign: (assignee: string | null, assigneeName?: string) => Promise<any> | void;
  onUpdate: (body: string, internal: boolean, emailData?: any) => void;
  onRelate: (targetId: string) => void;
  onMerge: (intoId: string) => void;
  onAddFile: (file: File) => void;
  onRemoveAttachment: (id: string) => void;
  onResolve: (state: string, code: string, notes: string) => void;
  onSetParent: (parentId: string | null) => void;
  onRequestApproval: (approver: string) => void;
  onDecideApproval: (approvalId: string, decision: 'approved' | 'rejected', note?: string) => void;
  onUpdateField: (field: string, value: any) => void;
};

function fmtBytes(n: number) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

/** Render text with @mentions highlighted. */
function withMentions(text: string) {
  return text.split(/(@[A-Za-z][\w.]*)/g).map((part, i) =>
    part.startsWith('@') ? (
      <span key={i} className="rounded bg-primary/15 px-1 font-medium text-primary">
        {part}
      </span>
    ) : (
      <span key={i}>{part}</span>
    ),
  );
}

/** The focus surface — one ticket fully expanded, reading like a document. */
export function TicketDocument({
  ticket,
  now,
  actions,
  allTickets = [],
  customFields = [],
}: {
  ticket: Ticket;
  now: number;
  actions?: TicketActions;
  allTickets?: Ticket[];
  customFields?: CustomField[];
}) {
  const heat = heatOf(ticket, now);
  const stop = thermal(heat);
  const color = thermalColor(heat);
  const [statusOpen, setStatusOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [replyMode, setReplyMode] = useState<'public' | 'internal' | 'email'>('public');
  const [isAssigning, setIsAssigning] = useState(false);
  const [picker, setPicker] = useState<null | 'link' | 'merge' | 'parent'>(null);
  const [pq, setPq] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const attachments = ticket.attachments ?? [];
  const [resolveState, setResolveState] = useState<string | null>(null);
  const [resCode, setResCode] = useState(RESOLUTION_CODES[0]);
  const [resNotes, setResNotes] = useState('');
  const [macroOpen, setMacroOpen] = useState(false);
  const [macroMgr, setMacroMgr] = useState(false);
  const { macros } = useMacros();
  const { session } = useAuth();
  const { role } = useRole();
  const { profiles } = useUsers();
  
  const isTech = ['Super Admin', 'Admin', 'Service Desk Agent', 'Agent', 'Technician'].includes(role as string);
  
  // Email Composer State
  const [emailTo, setEmailTo] = useState<string[]>([ticket.requesterEmail || ticket.requester]);
  const [emailCc, setEmailCc] = useState<string[]>(() => 
    Array.isArray(ticket.custom?.cc) ? (ticket.custom.cc as string[]) : []
  );
  const [emailBcc, setEmailBcc] = useState<string[]>(() => 
    Array.isArray(ticket.custom?.bcc) ? (ticket.custom.bcc as string[]) : []
  );
  const [showCc, setShowCc] = useState(() => Array.isArray(ticket.custom?.cc) && ticket.custom.cc.length > 0);
  const [showBcc, setShowBcc] = useState(false);
  const [emailSubject, setEmailSubject] = useState(`Re: [${ticket.ref}] ${ticket.subject}`);
  const [emailFrom, setEmailFrom] = useState('support@company.com (IT Support)');
  const { mailboxes } = useSettings();
  
  const [approver, setApprover] = useState('');
  const approvals = ticket.approvals ?? [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sentinel = (ticket.custom as Record<string, any> | undefined)?.sentinel as
    | {
        workspace?: string; client?: string; status?: string; severity?: string; url?: string;
        owner?: string; tactics?: string[]; techniques?: string[]; alertProductNames?: string[];
        alertsCount?: number; firstActivityTimeUtc?: string; lastActivityTimeUtc?: string;
        incidentNumber?: number; classification?: string;
        entities?: { kind: string; name: string }[];
      }
    | undefined;

  const applyMacro = (m: { body: string; setStatus?: string }) => {
    setDraft(applyVars(m.body, ticket));
    setMacroOpen(false);
    if (m.setStatus && actions) {
      if (m.setStatus === 'Resolved' || m.setStatus === 'Closed') setResolveState(m.setStatus);
      else actions.onStatus(m.setStatus);
    }
  };

  const parent = ticket.parentId ? allTickets.find((t) => t.id === ticket.parentId) : null;
  const children = allTickets.filter((t) => t.parentId === ticket.id);

  const related = (ticket.links ?? [])
    .map((id) => allTickets.find((t) => t.id === id))
    .filter(Boolean) as Ticket[];
  const mergedInto = ticket.mergedInto ? allTickets.find((t) => t.id === ticket.mergedInto) : null;
  const candidates = allTickets
    .filter((t) => t.id !== ticket.id && !(ticket.links ?? []).includes(t.id))
    .filter((t) => !pq || `${t.ref} ${t.subject}`.toLowerCase().includes(pq.toLowerCase()))
    .slice(0, 8);

  // Resolve effective assignee ID and name robustly
  const effectiveAssigneeId = useMemo(() => {
    if (ticket.assigneeId && ticket.assigneeId !== '00000000-0000-0000-0000-000000000000') {
      return ticket.assigneeId;
    }
    if (ticket.assignee && ticket.assignee !== 'Unassigned' && ticket.assignee !== '—') {
      const match = profiles.find(
        (p) =>
          p.id === ticket.assignee ||
          p.full_name?.toLowerCase().trim() === ticket.assignee?.toLowerCase().trim() ||
          p.email?.toLowerCase().trim() === ticket.assignee?.toLowerCase().trim()
      );
      if (match) return match.id;
      return ticket.assignee;
    }
    return '';
  }, [ticket.assigneeId, ticket.assignee, profiles]);

  const effectiveAssigneeName = useMemo(() => {
    if (ticket.assignee && ticket.assignee !== 'Unassigned' && ticket.assignee !== '—') {
      return ticket.assignee;
    }
    if (effectiveAssigneeId) {
      const match = profiles.find((p) => p.id === effectiveAssigneeId);
      if (match) return match.full_name || match.email;
    }
    return 'Unassigned';
  }, [ticket.assignee, effectiveAssigneeId, profiles]);

  return (
    <div className="relative mx-auto w-full px-10 py-8">
      {/* warmth edge running down the document */}
      <span
        className="absolute left-0 top-8 bottom-8 w-[3px] rounded-full"
        style={{ background: color }}
      />

      {/* meta line */}
      <div className="flex items-center gap-2.5 font-mono text-xs text-muted-foreground">
        <span className="text-foreground">{ticket.ref}</span>
        <span className="text-muted-foreground/50">·</span>
        <span>{ticket.module}</span>
        <span className="text-muted-foreground/50">·</span>
        <span>{ticket.priority}</span>
        {ticket.source === 'Email' && (
          <>
            <span className="text-muted-foreground/50">·</span>
            <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" /> Email</span>
          </>
        )}
        <span
          className="ml-auto inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px]"
          style={{ color, background: 'color-mix(in oklab, ' + color + ' 14%, transparent)' }}
        >
          <span className="h-1.5 w-1.5 rounded-full" style={{ background: color }} />
          {stop.label}
        </span>
      </div>

      {/* subject — expressive display */}
      <h1 className="mt-3 font-display text-[28px] font-medium leading-tight tracking-tight">
        {ticket.subject}
      </h1>

      {/* controls — status + assignee (functional) */}
      {actions && isTech ? (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <div className="relative">
            <button
              onClick={() => setStatusOpen((o) => !o)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-2.5 py-1.5 text-sm font-medium transition hover:bg-muted"
            >
              {ticket.state}
              <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
            {statusOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setStatusOpen(false)} />
                <div className="absolute left-0 z-20 mt-1 w-44 overflow-hidden rounded-lg border border-border bg-card py-1 shadow-lg">
                  {STATES.map((s) => (
                    <button
                      key={s}
                      onClick={() => {
                        setStatusOpen(false);
                        if (s === 'Resolved' || s === 'Closed') setResolveState(s);
                        else actions.onStatus(s);
                      }}
                      className={cn(
                        'flex w-full items-center px-3 py-1.5 text-left text-sm transition hover:bg-muted',
                        s === ticket.state && 'bg-muted/60',
                      )}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
          {!effectiveAssigneeId && session?.user?.id && (
            <button
              disabled={isAssigning}
              onClick={async () => {
                const name = session.user?.name || session.user?.email || session.user!.id;
                try {
                  setIsAssigning(true);
                  await actions.onAssign(session.user!.id, name);
                } finally {
                  setIsAssigning(false);
                }
              }}
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground transition hover:opacity-90 disabled:opacity-50"
            >
              {isAssigning ? 'Saving...' : 'Take Ownership'}
            </button>
          )}
          <div className="relative inline-flex items-center">
            <select
              aria-label="Assignee"
              disabled={isAssigning}
              value={effectiveAssigneeId}
              onChange={async (e) => {
                const val = e.target.value;
                const targetProfile = profiles.find((p) => p.id === val);
                const name = val ? targetProfile?.full_name || targetProfile?.email || val : undefined;
                try {
                  setIsAssigning(true);
                  await actions.onAssign(val || null, name);
                } finally {
                  setIsAssigning(false);
                }
              }}
              className="h-9 rounded-lg border border-input bg-background px-2 text-sm outline-none focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="">Unassigned</option>
              {effectiveAssigneeId && !profiles.some((p) => p.id === effectiveAssigneeId) && (
                <option value={effectiveAssigneeId}>
                  {effectiveAssigneeName}
                </option>
              )}
              {profiles
                .filter((p) => {
                  const norm = (p.role || '').toLowerCase();
                  return ['technician', 'agent', 'super admin', 'admin', 'service desk agent', 'manager'].some(r => norm.includes(r));
                })
                .map((p) => (
                <option key={p.id} value={p.id}>{p.full_name || p.email}</option>
              ))}
            </select>
            {isAssigning && (
              <span className="ml-2 text-xs text-muted-foreground animate-pulse">Saving...</span>
            )}
          </div>
        </div>
      ) : (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-2.5 py-1 text-sm font-medium">
            {ticket.state}
          </span>
          {effectiveAssigneeId && (
            <span className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-2.5 py-1 text-sm text-muted-foreground">
              Assignee: {effectiveAssigneeName}
            </span>
          )}
        </div>
      )}

      {/* resolution prompt */}
      {resolveState && actions && (
        <div className="mt-4 space-y-2 rounded-xl border border-primary/30 bg-primary/5 p-4">
          <div className="text-sm font-medium">Resolve as “{resolveState}”</div>
          <select
            value={resCode}
            onChange={(e) => setResCode(e.target.value)}
            className="h-9 w-full rounded-lg border border-input bg-background px-2 text-sm outline-none focus:border-primary"
          >
            {RESOLUTION_CODES.map((c) => <option key={c}>{c}</option>)}
          </select>
          <textarea
            value={resNotes}
            onChange={(e) => setResNotes(e.target.value)}
            rows={2}
            placeholder="Closure notes…"
            className="w-full resize-y rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary"
          />
          <div className="flex gap-2">
            <button
              onClick={() => { actions.onResolve(resolveState, resCode, resNotes.trim()); setResolveState(null); setResNotes(''); }}
              className="rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground transition hover:opacity-90"
            >
              Confirm {resolveState.toLowerCase()}
            </button>
            <button onClick={() => setResolveState(null)} className="rounded-lg border border-border px-3 py-1.5 text-sm font-medium transition hover:bg-muted">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* resolution summary */}
      {ticket.resolutionCode && (
        <div className="mt-4 rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-3 py-2 text-sm">
          <span className="font-medium text-emerald-600 dark:text-emerald-400">Resolved · {ticket.resolutionCode}</span>
          {ticket.closureNotes && <span className="text-muted-foreground"> — {ticket.closureNotes}</span>}
        </div>
      )}

      {/* parent banner */}
      {parent && (
        <div className="mt-4 flex items-center gap-2 rounded-lg border border-border bg-muted/60 px-3 py-2 text-sm">
          <GitMerge className="h-4 w-4 -rotate-90 text-muted-foreground" />
          Sub-ticket of <span className="font-mono">{parent.ref}</span>
          <span className="truncate text-muted-foreground">— {parent.subject}</span>
        </div>
      )}

      {/* merged banner */}
      {mergedInto && (
        <div className="mt-4 flex items-center gap-2 rounded-lg border border-border bg-muted/60 px-3 py-2 text-sm">
          <GitMerge className="h-4 w-4 text-muted-foreground" />
          Merged into <span className="font-mono">{mergedInto.ref}</span>
          <span className="truncate text-muted-foreground">— {mergedInto.subject}</span>
        </div>
      )}

      {/* related tickets */}
      {related.length > 0 && (
        <div className="mt-4">
          <SectionAnchor>Related</SectionAnchor>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {related.map((r) => (
              <span key={r.id} className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-2 py-1 text-xs">
                <Link2 className="h-3 w-3 text-muted-foreground" />
                <span className="font-mono text-[11px]">{r.ref}</span>
                <span className="max-w-[14rem] truncate text-muted-foreground">{r.subject}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* sub-tickets (epic children) */}
      {children.length > 0 && (
        <div className="mt-4">
          <SectionAnchor>Sub-tickets ({children.length})</SectionAnchor>
          <div className="mt-2 space-y-1">
            {children.map((c) => (
              <div key={c.id} className="flex items-center gap-2 rounded-md border border-border bg-card px-2.5 py-1.5 text-sm">
                <GitMerge className="h-3.5 w-3.5 -rotate-90 text-muted-foreground" />
                <span className="font-mono text-[11px] text-muted-foreground">{c.ref}</span>
                <span className="flex-1 truncate">{c.subject}</span>
                <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">{c.state}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Microsoft Sentinel incident panel */}
      {sentinel && (
        <div className="mt-5 rounded-xl border border-primary/25 bg-primary/[0.04] p-4">
          <div className="flex flex-wrap items-center gap-2">
            <Shield className="h-4 w-4 text-primary" />
            <span className="text-sm font-semibold">Microsoft Sentinel</span>
            {sentinel.workspace && <span className="rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">{sentinel.workspace}</span>}
            {sentinel.severity && <span className="rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">{sentinel.severity}</span>}
            {sentinel.status && <span className="rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">{sentinel.status}</span>}
            {sentinel.url && (
              <a href={sentinel.url} target="_blank" rel="noreferrer" className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-2.5 py-1 text-xs font-medium transition hover:bg-muted">
                <ExternalLink className="h-3.5 w-3.5" /> Open in Azure
              </a>
            )}
          </div>

          <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
            <SentinelStat label="Incident #" value={sentinel.incidentNumber != null ? `#${sentinel.incidentNumber}` : '—'} />
            <SentinelStat label="Owner" value={sentinel.owner ?? 'Unassigned'} />
            <SentinelStat label="Alerts" value={String(sentinel.alertsCount ?? 0)} />
            <SentinelStat label="Classification" value={sentinel.classification ?? '—'} />
          </div>

          {sentinel.tactics && sentinel.tactics.length > 0 && (
            <ChipRow label="Tactics" items={sentinel.tactics} />
          )}
          {sentinel.alertProductNames && sentinel.alertProductNames.length > 0 && (
            <ChipRow label="Detected by" items={sentinel.alertProductNames} />
          )}
          {sentinel.entities && sentinel.entities.length > 0 && (
            <div className="mt-3">
              <div className="mb-1 text-[11px] uppercase tracking-wide text-muted-foreground">Entities</div>
              <div className="flex flex-wrap gap-1.5">
                {sentinel.entities.slice(0, 24).map((e, i) => (
                  <span key={i} className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2 py-0.5 text-xs">
                    <span className="text-muted-foreground">{e.kind}:</span> {e.name}
                  </span>
                ))}
              </div>
            </div>
          )}
          {(sentinel.firstActivityTimeUtc || sentinel.lastActivityTimeUtc) && (
            <div className="mt-3 font-mono text-[11px] text-muted-foreground">
              Activity: {sentinel.firstActivityTimeUtc ? new Date(sentinel.firstActivityTimeUtc).toLocaleString() : '—'}
              {' → '}
              {sentinel.lastActivityTimeUtc ? new Date(sentinel.lastActivityTimeUtc).toLocaleString() : '—'}
            </div>
          )}
        </div>
      )}

      {/* timeline spine */}
      <div className="mt-5 neo rounded-[2rem] px-4 py-4">
        <TimelineSpine
          createdAt={ticket.createdAt}
          slaDueAt={ticket.slaDueAt}
          events={ticket.events}
          now={now}
          heat={heat}
        />
      </div>

      {/* AI assist strip — the layer that goes beyond ServiceNow */}
      <div className="mt-5 flex items-start gap-3 neo-inset rounded-xl px-4 py-4 border-none">
        <Sparkles className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
        <div className="text-sm">
          <span className="font-medium">Suggested resolution</span>
          <p className="mt-0.5 text-muted-foreground">
            Two past tickets with this signature were resolved by rolling back the
            recent deploy and clearing the connection pool. Draft a response?
          </p>
          <div className="mt-2 flex gap-2">
            <button
              onClick={() =>
                setDraft(
                  `Investigating ${ticket.ref}. Based on similar past tickets, rolling back the recent change and clearing the connection pool. Will update shortly.`,
                )
              }
              className="rounded-lg bg-primary px-2.5 py-1 text-xs font-medium text-primary-foreground transition hover:opacity-90"
            >
              Draft reply
            </button>
            <button className="rounded-lg border border-border px-2.5 py-1 text-xs font-medium transition hover:bg-muted">
              View similar
            </button>
          </div>
        </div>
      </div>

      {/* narrative body */}
      <div className="mt-6">
        <SectionAnchor>Summary</SectionAnchor>
        <p className="mt-2 text-[15px] leading-relaxed text-foreground/90">
          {ticket.description}
        </p>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
        <Field icon={<User className="h-3.5 w-3.5" />} label="Requester" value={ticket.requesterEmail ? `${ticket.requester} (${ticket.requesterEmail})` : ticket.requester} mono />
        <Field icon={<User className="h-3.5 w-3.5" />} label="Assignee" value={effectiveAssigneeName} />
        {Array.isArray(ticket.custom?.cc) && (ticket.custom.cc as string[]).length > 0 ? (
          <div className="col-span-2">
            <Field icon={<Mail className="h-3.5 w-3.5" />} label="CC Recipients" value={(ticket.custom.cc as string[]).join(', ')} mono />
          </div>
        ) : null}
        <EditableField icon={<Tag className="h-3.5 w-3.5" />} label="Category" value={ticket.category} type="select" options={['Hardware', 'Software', 'Network', 'Access', 'Security', 'Other']} onChange={actions && isTech ? (v) => actions.onUpdateField('category', v) : undefined} />
        <EditableField icon={<Box className="h-3.5 w-3.5" />} label="Affected CI" value={ticket.ci} type="text" onChange={actions && isTech ? (v) => actions.onUpdateField('ci', v) : undefined} />
        <EditableField icon={<Gauge className="h-3.5 w-3.5" />} label="Impact" value={ticket.impact} type="select" options={['High', 'Medium', 'Low']} onChange={actions && isTech ? (v) => actions.onUpdateField('impact', v) : undefined} />
        <EditableField icon={<Gauge className="h-3.5 w-3.5" />} label="Urgency" value={ticket.urgency} type="select" options={['High', 'Medium', 'Low']} onChange={actions && isTech ? (v) => actions.onUpdateField('urgency', v) : undefined} />
        <EditableField icon={<Clock className="h-3.5 w-3.5" />} label="Due" value={ticket.dueAt} type="date" onChange={actions && isTech ? (v) => actions.onUpdateField('dueAt', v) : undefined} />
        {ticket.responseDueAt && (
          <Field icon={<Clock className="h-3.5 w-3.5" />} label="Response SLA" value={new Date(ticket.responseDueAt).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })} mono />
        )}
        {ticket.watchers && ticket.watchers.length > 0 && (
          <Field icon={<Eye className="h-3.5 w-3.5" />} label="Watchers" value={ticket.watchers.join(', ')} />
        )}
        {customFields
          .filter((f) => {
            const v = ticket.custom?.[f.key];
            return v !== undefined && v !== '' && v !== false;
          })
          .map((f) => {
            const v = ticket.custom?.[f.key];
            return (
              <Field
                key={f.id}
                icon={<Tag className="h-3.5 w-3.5" />}
                label={f.label}
                value={f.type === 'checkbox' ? 'Yes' : String(v)}
              />
            );
          })}
      </div>

      {/* approvals */}
      {actions && (ticket.module === 'Service Request' || ticket.module === 'Change' || approvals.length > 0) && (
        <div className="mt-7">
          <SectionAnchor>Approvals</SectionAnchor>
          <div className="mt-2 space-y-1.5">
            {approvals.map((a) => (
              <div key={a.id} className="flex flex-wrap items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm">
                <User className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="font-medium">{a.approver}</span>
                <span
                  className={cn(
                    'rounded px-1.5 py-0.5 text-[10px] font-medium capitalize',
                    a.status === 'approved' && 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
                    a.status === 'rejected' && 'bg-temp-ember/15 text-temp-ember',
                    a.status === 'pending' && 'bg-amber-500/15 text-amber-600 dark:text-amber-400',
                  )}
                >
                  {a.status}
                </span>
                {a.note && <span className="text-muted-foreground">— {a.note}</span>}
                {a.status === 'pending' && (
                  <div className="ml-auto flex gap-1.5">
                    <button onClick={() => actions.onDecideApproval(a.id, 'approved')} className="rounded-md bg-emerald-500/15 px-2 py-1 text-xs font-medium text-emerald-600 transition hover:bg-emerald-500/25 dark:text-emerald-400">Approve</button>
                    <button onClick={() => actions.onDecideApproval(a.id, 'rejected')} className="rounded-md bg-temp-ember/15 px-2 py-1 text-xs font-medium text-temp-ember transition hover:bg-temp-ember/25">Reject</button>
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="mt-2 flex items-center gap-2">
            <select value={approver} onChange={(e) => setApprover(e.target.value)} className="h-8 rounded-md border border-input bg-background px-2 text-sm outline-none focus:border-primary">
              <option value="">Select approver...</option>
              {profiles.map((p) => {
                const name = p.full_name || p.display_name || p.email;
                return <option key={p.id} value={name}>{name} ({p.role})</option>;
              })}
            </select>
            <button 
              disabled={!approver}
              onClick={() => approver && actions.onRequestApproval(approver)} 
              className="rounded-lg border border-border px-3 py-1.5 text-sm font-medium transition hover:bg-muted disabled:opacity-50"
            >
              Request approval
            </button>
          </div>
        </div>
      )}

      {/* attachments */}
      {actions && (
        <div className="mt-7">
          <div className="flex items-center justify-between">
            <SectionAnchor>Attachments</SectionAnchor>
            <button
              onClick={() => fileRef.current?.click()}
              className="inline-flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1 text-xs font-medium transition hover:bg-muted"
            >
              <Paperclip className="h-3.5 w-3.5" /> Add file
            </button>
            <input
              ref={fileRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => {
                Array.from(e.target.files ?? []).forEach((f) => actions.onAddFile(f));
                e.target.value = '';
              }}
            />
          </div>
          {attachments.length === 0 ? (
            <div
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                Array.from(e.dataTransfer.files).forEach((f) => actions.onAddFile(f));
              }}
              className="mt-2 cursor-pointer rounded-xl neo-inset border-none py-6 text-center text-sm text-muted-foreground transition hover:ring-2 hover:ring-[var(--color-primary)]"
            >
              Drop files here, or click to upload
            </div>
          ) : (
            <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
              {attachments.map((a) => (
                <div key={a.id} className="group relative overflow-hidden rounded-lg border border-border">
                  {a.type.startsWith('image/') ? (
                    <img src={a.url} alt={a.name} className="h-24 w-full object-cover" />
                  ) : (
                    <div className="flex h-24 items-center justify-center bg-muted">
                      <FileText className="h-7 w-7 text-muted-foreground" />
                    </div>
                  )}
                  <div className="flex items-center gap-1.5 px-2 py-1.5">
                    <span className="flex-1 truncate text-xs">{a.name}</span>
                    <span className="font-mono text-[10px] text-muted-foreground">{fmtBytes(a.size)}</span>
                  </div>
                  <div className="absolute right-1 top-1 flex gap-1 opacity-0 transition group-hover:opacity-100">
                    <a href={a.url} download={a.name} className="flex h-6 w-6 items-center justify-center rounded-md bg-card text-muted-foreground hover:text-foreground" title="Download">
                      <Download className="h-3.5 w-3.5" />
                    </a>
                    <button onClick={() => actions.onRemoveAttachment(a.id)} className="flex h-6 w-6 items-center justify-center rounded-md bg-card text-muted-foreground hover:text-temp-ember" title="Remove">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* activity */}
      <div className="mt-7">
        <SectionAnchor>Timeline</SectionAnchor>
        <ol className="mt-3 space-y-3">
          {[...ticket.events].reverse().map((e, i) => (
            <li key={i} className="flex gap-3">
              <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-muted-foreground/60" />
              <div className="flex-1">
                {e.kind === 'comment' ? (
                  <div className={cn('rounded-lg border px-3 py-2 text-sm', e.internal ? 'border-amber-500/30 bg-amber-500/5' : 'border-border bg-muted/40')}>
                    <div className="mb-1.5 flex items-center gap-2 flex-wrap">
                      {e.internal && (
                        <span className="inline-flex items-center gap-1 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-medium text-amber-600 dark:text-amber-400">
                          <Lock className="h-3 w-3" /> Internal note
                        </span>
                      )}
                      {e.source === 'email' ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 border border-emerald-500/25 px-2 py-0.5 text-[10px] font-semibold text-emerald-600 dark:text-emerald-400">
                          <Mail className="h-3 w-3" /> Email Reply
                        </span>
                      ) : e.source === 'system' ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-slate-500/10 border border-slate-500/20 px-2 py-0.5 text-[10px] font-semibold text-slate-600 dark:text-slate-400">
                          <Zap className="h-3 w-3" /> System
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-blue-500/10 border border-blue-500/20 px-2 py-0.5 text-[10px] font-semibold text-blue-600 dark:text-blue-400">
                          <Globe className="h-3 w-3" /> Portal
                        </span>
                      )}
                    </div>
                    <div>{withMentions(e.label)}</div>
                  </div>
                ) : (
                  <div className="text-sm">{e.label}</div>
                )}
                <div className="mt-0.5 font-mono text-[11px] text-muted-foreground">
                  {e.actor ? `${e.actor === '00000000-0000-0000-0000-000000000000' ? 'System' : profiles.find(p => p.id === e.actor)?.full_name || profiles.find(p => p.id === e.actor)?.email || e.actor} · ` : ''}
                  {new Date(e.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            </li>
          ))}
        </ol>
      </div>

      {/* composer (functional) */}
      {actions && (
        <div className="mt-6 border-t border-border pt-4">
          {/* reply vs internal-note vs email toggle */}
          <div className="mb-2 flex w-fit rounded-lg border border-border p-0.5 text-xs">
            <button
              onClick={() => setReplyMode('public')}
              className={cn('flex items-center gap-1 rounded-md px-2.5 py-1 font-medium transition', replyMode === 'public' ? 'bg-muted text-foreground' : 'text-muted-foreground')}
            >
              <MessageSquare className="h-3 w-3" /> Reply
            </button>
            <button
              onClick={() => setReplyMode('email')}
              className={cn('flex items-center gap-1 rounded-md px-2.5 py-1 font-medium transition', replyMode === 'email' ? 'bg-primary/10 text-primary' : 'text-muted-foreground')}
            >
              <Mail className="h-3 w-3" /> Email
            </button>
            <button
              onClick={() => setReplyMode('internal')}
              className={cn('flex items-center gap-1 rounded-md px-2.5 py-1 font-medium transition', replyMode === 'internal' ? 'bg-amber-500/15 text-amber-600 dark:text-amber-400' : 'text-muted-foreground')}
            >
              <Lock className="h-3 w-3" /> Internal note
            </button>
          </div>

          {replyMode === 'email' && (
            <div className="mb-3 flex flex-col gap-3 rounded-lg border border-border bg-muted/30 p-3 text-sm">
              <div className="flex items-center gap-4">
                <span className="w-16 font-medium text-muted-foreground">From</span>
                <select 
                  value={emailFrom}
                  onChange={(e) => setEmailFrom(e.target.value)}
                  className="flex-1 rounded-md border border-border bg-background px-3 py-1.5 outline-none focus:border-primary"
                >
                  {mailboxes?.map(mbx => (
                    <option key={mbx.id} value={`${mbx.email} (${mbx.name})`}>{mbx.email} ({mbx.name})</option>
                  ))}
                  {!mailboxes?.length && <option value="support@company.com">support@company.com</option>}
                </select>
              </div>
              
              <div className="flex items-start gap-4">
                <span className="mt-2 w-16 font-medium text-muted-foreground">To</span>
                <div className="flex flex-1 items-center gap-2">
                  <div className="flex min-h-[36px] flex-1 flex-wrap gap-2 rounded-md border border-border bg-background px-2 py-1.5">
                    {emailTo.map((email, idx) => (
                      <span key={idx} className="flex items-center gap-1.5 rounded-full bg-muted px-2 py-0.5 text-xs">
                        {email} <button onClick={() => setEmailTo(emailTo.filter(e => e !== email))}><X className="h-3 w-3 text-muted-foreground hover:text-foreground" /></button>
                      </span>
                    ))}
                    <input type="text" placeholder="+ Add Email" className="min-w-[100px] flex-1 bg-transparent text-sm outline-none" onKeyDown={(e) => {
                      if (e.key === 'Enter' && e.currentTarget.value) {
                        setEmailTo([...emailTo, e.currentTarget.value]);
                        e.currentTarget.value = '';
                      }
                    }}/>
                  </div>
                  <div className="flex items-center gap-2 text-xs font-medium text-primary">
                    {!showCc && <button onClick={() => setShowCc(true)}>CC</button>}
                    {!showBcc && <button onClick={() => setShowBcc(true)}>BCC</button>}
                  </div>
                </div>
              </div>

              {showCc && (
                <div className="flex items-start gap-4">
                  <span className="mt-2 w-16 font-medium text-muted-foreground">CC</span>
                  <div className="flex min-h-[36px] flex-1 flex-wrap gap-2 rounded-md border border-border bg-background px-2 py-1.5">
                    {emailCc.map((email, idx) => (
                      <span key={idx} className="flex items-center gap-1.5 rounded-full bg-muted px-2 py-0.5 text-xs">
                        {email} <button onClick={() => setEmailCc(emailCc.filter(e => e !== email))}><X className="h-3 w-3 text-muted-foreground hover:text-foreground" /></button>
                      </span>
                    ))}
                    <input type="text" placeholder="+ Add Email" className="min-w-[100px] flex-1 bg-transparent text-sm outline-none" onKeyDown={(e) => {
                      if (e.key === 'Enter' && e.currentTarget.value) {
                        setEmailCc([...emailCc, e.currentTarget.value]);
                        e.currentTarget.value = '';
                      }
                    }}/>
                  </div>
                </div>
              )}

              {showBcc && (
                <div className="flex items-start gap-4">
                  <span className="mt-2 w-16 font-medium text-muted-foreground">BCC</span>
                  <div className="flex min-h-[36px] flex-1 flex-wrap gap-2 rounded-md border border-border bg-background px-2 py-1.5">
                    {emailBcc.map((email, idx) => (
                      <span key={idx} className="flex items-center gap-1.5 rounded-full bg-muted px-2 py-0.5 text-xs">
                        {email} <button onClick={() => setEmailBcc(emailBcc.filter(e => e !== email))}><X className="h-3 w-3 text-muted-foreground hover:text-foreground" /></button>
                      </span>
                    ))}
                    <input type="text" placeholder="+ Add Email" className="min-w-[100px] flex-1 bg-transparent text-sm outline-none" onKeyDown={(e) => {
                      if (e.key === 'Enter' && e.currentTarget.value) {
                        setEmailBcc([...emailBcc, e.currentTarget.value]);
                        e.currentTarget.value = '';
                      }
                    }}/>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-4">
                <span className="w-16 font-medium text-muted-foreground">Subject</span>
                <input 
                  type="text" 
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  className="flex-1 rounded-md border border-border bg-background px-3 py-1.5 outline-none focus:border-primary" 
                />
              </div>
            </div>
          )}

          <div className="flex flex-col overflow-hidden rounded-xl neo-inset border-none bg-transparent transition focus-within:ring-2 focus-within:ring-[var(--color-primary)]/50">
            <div className="flex items-center justify-between border-b border-border/50 px-3 py-1.5 text-muted-foreground">
              <div className="flex items-center gap-1">
                <button className="rounded p-1 hover:bg-muted"><Bold className="h-3.5 w-3.5" /></button>
                <button className="rounded p-1 hover:bg-muted"><Italic className="h-3.5 w-3.5" /></button>
                <button className="rounded p-1 hover:bg-muted"><Underline className="h-3.5 w-3.5" /></button>
                <div className="mx-1 h-3.5 w-px bg-border" />
                <button className="rounded p-1 hover:bg-muted"><List className="h-3.5 w-3.5" /></button>
                <button className="rounded p-1 hover:bg-muted"><List className="h-3.5 w-3.5 rotate-180" /></button>
                <div className="mx-1 h-3.5 w-px bg-border" />
                <button className="rounded p-1 hover:bg-muted"><Link2 className="h-3.5 w-3.5" /></button>
                <button className="rounded p-1 hover:bg-muted"><Smile className="h-3.5 w-3.5" /></button>
                <button className="rounded p-1 hover:bg-muted"><ImageIcon className="h-3.5 w-3.5" /></button>
                <button className="rounded p-1 hover:bg-muted"><Code className="h-3.5 w-3.5" /></button>
              </div>
              {replyMode === 'email' && (
                <button className="text-[11px] font-medium hover:text-foreground">Plain Text</button>
              )}
            </div>
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={replyMode === 'email' ? 6 : 2}
              placeholder={replyMode === 'internal' ? 'Internal note (not visible to requester). Use @name to mention…' : replyMode === 'email' ? 'Type your email here...' : 'Reply… use @name to mention'}
              className="w-full resize-y bg-transparent px-3 py-2 text-sm outline-none"
            />
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            {replyMode === 'email' ? (
              <>
                <button
                  onClick={() => {
                    if (!draft.trim()) return;
                    actions.onUpdate(draft.trim(), false, {
                      to: emailTo,
                      cc: emailCc,
                      bcc: emailBcc,
                      subject: emailSubject,
                      from: emailFrom
                    });
                    setDraft('');
                  }}
                  disabled={!draft.trim()}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-bold text-primary-foreground shadow-sm transition hover:opacity-90 disabled:opacity-50"
                >
                  <Send className="h-4 w-4" /> Send Email
                </button>
                <button className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm font-medium transition hover:bg-muted">
                  <Save className="h-4 w-4 text-muted-foreground" /> Save Draft
                </button>
                <button className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm font-medium transition hover:bg-muted">
                  <Eye className="h-4 w-4 text-muted-foreground" /> Preview
                </button>
                <label className="ml-2 flex cursor-pointer items-center gap-1.5 text-sm text-muted-foreground">
                  <input type="checkbox" className="rounded border-border accent-primary" />
                  Send copy
                </label>
              </>
            ) : (
              <button
                onClick={() => {
                  if (!draft.trim()) return;
                  actions.onUpdate(draft.trim(), replyMode === 'internal');
                  setDraft('');
                }}
                disabled={!draft.trim()}
                className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground transition hover:opacity-90 disabled:opacity-50"
              >
                <Send className="h-4 w-4" /> {replyMode === 'internal' ? 'Add note' : 'Post reply'}
              </button>
            )}

            <div className="ml-auto flex items-center gap-2">
              <button
                onClick={() => setMacroOpen((o) => !o)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-sm font-medium transition hover:bg-muted"
              >
                <Zap className="h-4 w-4" /> Macros <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
              {macroOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setMacroOpen(false)} />
                  <div className="absolute bottom-10 left-0 z-20 w-72 overflow-hidden rounded-lg border border-border bg-card py-1 shadow-lg">
                    {macros.map((m) => (
                      <button key={m.id} onClick={() => applyMacro(m)} className="flex w-full flex-col items-start px-3 py-1.5 text-left transition hover:bg-muted">
                        <span className="flex items-center gap-1.5 text-sm font-medium">
                          {m.name}
                          {m.setStatus && <span className="rounded bg-muted px-1 text-[10px] text-muted-foreground">→ {m.setStatus}</span>}
                        </span>
                        <span className="line-clamp-1 text-[11px] text-muted-foreground">{m.body}</span>
                      </button>
                    ))}
                    <button onClick={() => { setMacroOpen(false); setMacroMgr(true); }} className="mt-1 w-full border-t border-border px-3 py-1.5 text-left text-xs font-medium text-primary hover:bg-muted">
                      Manage macros…
                    </button>
                  </div>
                </>
              )}
            </div>
            <button
              onClick={() => { setPicker(picker === 'link' ? null : 'link'); setPq(''); }}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium transition',
                picker === 'link' ? 'border-primary bg-primary/5 text-primary' : 'border-border hover:bg-muted',
              )}
            >
              <Link2 className="h-4 w-4" /> Link
            </button>
            <button
              onClick={() => { setPicker(picker === 'merge' ? null : 'merge'); setPq(''); }}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium transition',
                picker === 'merge' ? 'border-primary bg-primary/5 text-primary' : 'border-border hover:bg-muted',
              )}
            >
              <GitMerge className="h-4 w-4" /> Merge
            </button>
            <button
              onClick={() => { setPicker(picker === 'parent' ? null : 'parent'); setPq(''); }}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium transition',
                picker === 'parent' ? 'border-primary bg-primary/5 text-primary' : 'border-border hover:bg-muted',
              )}
            >
              <GitMerge className="h-4 w-4 -rotate-90" /> Sub-ticket of
            </button>
          </div>

          {/* picker for link / merge / parent */}
          {picker && (
            <div className="mt-3 overflow-hidden rounded-xl border border-border">
              <input
                autoFocus
                value={pq}
                onChange={(e) => setPq(e.target.value)}
                placeholder={picker === 'link' ? 'Find a ticket to relate…' : picker === 'merge' ? 'Find a ticket to merge into…' : 'Find the parent ticket…'}
                className="h-10 w-full border-b border-border bg-background px-3 text-sm outline-none focus:border-primary"
              />
              <div className="max-h-56 overflow-y-auto">
                {candidates.length === 0 && (
                  <div className="px-3 py-4 text-center text-sm text-muted-foreground">No tickets found.</div>
                )}
                {candidates.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => {
                      if (picker === 'link') actions.onRelate(c.id);
                      else if (picker === 'merge') actions.onMerge(c.id);
                      else actions.onSetParent(c.id);
                      setPicker(null);
                    }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition hover:bg-muted/60"
                  >
                    <span className="font-mono text-[11px] text-muted-foreground">{c.ref}</span>
                    <span className="flex-1 truncate">{c.subject}</span>
                    <span className="truncate text-[11px] text-muted-foreground">{c.team}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <MacroManager open={macroMgr} onClose={() => setMacroMgr(false)} />
    </div>
  );
}

function SentinelStat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="truncate text-sm font-medium">{value}</div>
    </div>
  );
}

function ChipRow({ label, items }: { label: string; items: string[] }) {
  return (
    <div className="mt-3">
      <div className="mb-1 text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="flex flex-wrap gap-1.5">
        {items.map((it, i) => (
          <span key={i} className="rounded-md bg-muted px-2 py-0.5 text-xs">{it}</span>
        ))}
      </div>
    </div>
  );
}

function SectionAnchor({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="font-display text-[13px] font-semibold uppercase tracking-wide text-muted-foreground">
      {children}
    </h2>
  );
}


function EditableField({
  icon,
  label,
  value,
  type = 'text',
  options = [],
  onChange,
}: {
  icon: React.ReactNode;
  label: string;
  value: any;
  type?: 'text' | 'select' | 'date';
  options?: string[];
  onChange?: (val: any) => void;
}) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">
        {icon}
        {label}
      </div>
      {!onChange ? (
        <div className="mt-0.5">{type === 'date' && value ? new Date(value).toLocaleDateString() : (value || '—')}</div>
      ) : type === 'select' ? (
        <select
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className="mt-0.5 h-7 w-full rounded-md border border-transparent hover:border-input focus:border-primary bg-transparent px-1 text-sm outline-none transition"
        >
          <option value="">—</option>
          {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : type === 'date' ? (
        <input
          type="date"
          value={value ? new Date(value).toISOString().split('T')[0] : ''}
          onChange={(e) => onChange(e.target.value ? new Date(e.target.value).getTime() : null)}
          className="mt-0.5 h-7 w-full rounded-md border border-transparent hover:border-input focus:border-primary bg-transparent px-1 text-sm outline-none transition"
        />
      ) : (
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="—"
          className="mt-0.5 h-7 w-full rounded-md border border-transparent hover:border-input focus:border-primary bg-transparent px-1 text-sm outline-none transition"
        />
      )}
    </div>
  );
}

function Field({
  icon,
  label,
  value,
  mono,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">
        {icon}
        {label}
      </div>
      <div className={cn('mt-0.5', mono && 'font-mono text-[13px]')}>{value}</div>
    </div>
  );
}
